// ── Scope Overlay ──
// Manages the fullscreen sniper scope effect and scoped-weapon FOV zoom.
// - AWP / SSG08: full circular scope overlay with crosshair lines + mil-dots
// - AUG / SG553: subtle FOV zoom only (no overlay)
// - AWP supports double-scope (6x -> 12x), others are single-scope only.

import { WEAPONS, type WeaponId } from '../core/config';
import { isMobile } from '../core/device';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const NORMAL_FOV = 90;

/** Duration of the FOV lerp transition in seconds. */
export const SCOPE_LERP_DURATION = 0.15; // 150ms

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ScopeType = 'sniper' | 'rifle' | 'none';

export interface ScopeState {
  stage: number;       // 0 = unscoped, 1 = single scope, 2 = double scope (AWP only)
  weaponId: WeaponId | null;
  targetFov: number;
  showOverlay: boolean; // true only for sniper-type weapons
}

// ---------------------------------------------------------------------------
// Pure functions (testable without DOM)
// ---------------------------------------------------------------------------

/** Classify a weapon's scope type. */
export function getScopeType(weaponId: WeaponId): ScopeType {
  const cfg = WEAPONS[weaponId];
  if (!cfg) return 'none';
  if (!cfg.scopeZoom && weaponId !== 'awp' && weaponId !== 'ssg08') return 'none';
  if (cfg.type === 'sniper') return 'sniper';
  if (cfg.scopeZoom) return 'rifle';
  return 'none';
}

/** Whether this weapon supports double-scope (stage 2). Only AWP. */
export function hasDoubleScope(weaponId: WeaponId): boolean {
  return weaponId === 'awp';
}

/** Compute the target FOV for a weapon at a given scope stage. */
export function computeScopeFov(weaponId: WeaponId, stage: number): number {
  if (stage <= 0) return NORMAL_FOV;

  const cfg = WEAPONS[weaponId];
  const scopeZoom = cfg?.scopeZoom;
  if (!scopeZoom) return NORMAL_FOV;

  // Clamp stage for weapons without double scope
  const effectiveStage = hasDoubleScope(weaponId) ? stage : Math.min(stage, 1);

  if (effectiveStage === 1) {
    return NORMAL_FOV / scopeZoom;
  }
  if (effectiveStage === 2) {
    return NORMAL_FOV / (scopeZoom * 2);
  }
  return NORMAL_FOV;
}

/** Linear interpolation between two FOV values with clamped t. */
export function lerpFov(from: number, to: number, t: number): number {
  const ct = Math.max(0, Math.min(1, t));
  return from + (to - from) * ct;
}

/**
 * Pure state transition: advance the scope stage for a weapon.
 * Returns the new ScopeState after one toggle.
 */
export function advanceScopeStage(weaponId: WeaponId, currentStage: number): ScopeState {
  const scopeType = getScopeType(weaponId);

  // Non-scoped weapons: always stay at stage 0
  if (scopeType === 'none') {
    return { stage: 0, weaponId: null, targetFov: NORMAL_FOV, showOverlay: false };
  }

  const maxStage = hasDoubleScope(weaponId) ? 2 : 1;
  const nextStage = currentStage + 1;

  // Cycle: 0 -> 1 -> (2 for AWP) -> 0
  if (nextStage > maxStage) {
    return { stage: 0, weaponId: null, targetFov: NORMAL_FOV, showOverlay: false };
  }

  return {
    stage: nextStage,
    weaponId,
    targetFov: computeScopeFov(weaponId, nextStage),
    showOverlay: scopeType === 'sniper',
  };
}

// ---------------------------------------------------------------------------
// ScopeOverlay class (DOM overlay for sniper scopes)
// ---------------------------------------------------------------------------

export class ScopeOverlay {
  private el: HTMLElement;
  private breathHint: HTMLElement;
  private active = false;
  private stage = 0;
  private currentWeapon: WeaponId | null = null;

  constructor() {
    this.el = document.createElement('div');
    Object.assign(this.el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      zIndex: '40',
      pointerEvents: 'none',
      display: 'none',
    });

    // Black overlay with circular transparent cutout
    const innerStop = isMobile() ? '28vmin' : '18vmin';
    const outerStop = isMobile() ? '34vmin' : '22vmin';
    const scope = document.createElement('div');
    Object.assign(scope.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: `radial-gradient(circle at 50% 50%, transparent ${innerStop}, rgba(0,0,0,0.98) ${outerStop})`,
    });
    this.el.appendChild(scope);

    // Horizontal crosshair line
    const hLine = document.createElement('div');
    Object.assign(hLine.style, {
      position: 'absolute',
      top: '50%',
      left: '0',
      width: '100%',
      height: '1px',
      background: 'rgba(0,0,0,0.8)',
      transform: 'translateY(-0.5px)',
    });
    this.el.appendChild(hLine);

    // Vertical crosshair line
    const vLine = document.createElement('div');
    Object.assign(vLine.style, {
      position: 'absolute',
      top: '0',
      left: '50%',
      width: '1px',
      height: '100%',
      background: 'rgba(0,0,0,0.8)',
      transform: 'translateX(-0.5px)',
    });
    this.el.appendChild(vLine);

    // Mil-dot markers along vertical line
    const dotOffsets = [-12, -8, -4, 4, 8, 12]; // vmin from center
    for (const offset of dotOffsets) {
      const dot = document.createElement('div');
      Object.assign(dot.style, {
        position: 'absolute',
        left: '50%',
        top: `calc(50% + ${offset}vmin)`,
        width: '6px',
        height: '2px',
        background: 'rgba(0,0,0,0.8)',
        transform: 'translate(-50%, -50%)',
      });
      this.el.appendChild(dot);
    }

    document.body.appendChild(this.el);

    this.breathHint = document.createElement('div');
    Object.assign(this.breathHint.style, {
      position: 'fixed',
      right: '24px',
      bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
      zIndex: '41',
      display: 'none',
      alignItems: 'center',
      gap: '8px',
      padding: '5px 8px',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: '3px',
      background: 'rgba(0,0,0,0.54)',
      color: 'rgba(255,255,255,0.86)',
      font: '700 11px "Courier New", monospace',
      letterSpacing: '1px',
      pointerEvents: 'none',
      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
    });
    const breathIcon = document.createElement('span');
    Object.assign(breathIcon.style, {
      width: '12px',
      height: '12px',
      border: '2px solid rgba(255,255,255,0.75)',
      borderRadius: '50%',
      boxSizing: 'border-box',
      boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.55)',
      flex: '0 0 auto',
    });
    const breathText = document.createElement('span');
    breathText.textContent = '[Shift] HOLD BREATH';
    this.breathHint.appendChild(breathIcon);
    this.breathHint.appendChild(breathText);
    if (typeof document !== 'undefined' && document.body && typeof document.body.appendChild === 'function') {
      document.body.appendChild(this.breathHint);
    }
  }

  // ---- Public API ----------------------------------------------------------

  /**
   * Enter or advance scope for a weapon (toggle behavior).
   * - First call: enter stage 1
   * - Second call (AWP only): enter stage 2 (double scope)
   * - Next call: exit scope
   * Non-scoped weapons are ignored.
   */
  enter(weaponId: WeaponId): void {
    const state = advanceScopeStage(weaponId, this.stage);

    this.stage = state.stage;
    this.currentWeapon = state.weaponId;
    this.active = state.stage > 0;

    // Show/hide the DOM overlay (only for sniper-type weapons)
    if (state.showOverlay && state.stage > 0) {
      this.el.style.display = 'block';
    } else {
      this.el.style.display = 'none';
    }
    this.updateBreathHint();
  }

  /** Exit scope completely, regardless of current stage. */
  exit(): void {
    this.stage = 0;
    this.currentWeapon = null;
    this.active = false;
    this.el.style.display = 'none';
    this.updateBreathHint();
  }

  /** Legacy show() for backward compatibility. Prefer enter(weaponId). */
  show(): void {
    this.el.style.display = 'block';
    this.active = true;
    this.updateBreathHint();
  }

  /** Legacy hide() for backward compatibility. Prefer exit(). */
  hide(): void {
    this.el.style.display = 'none';
    this.active = false;
    this.stage = 0;
    this.currentWeapon = null;
    this.updateBreathHint();
  }

  /** Whether any scope stage is active. */
  isActive(): boolean {
    return this.active;
  }

  /** Current scope stage (0 = unscoped, 1 = single, 2 = double). */
  getStage(): number {
    return this.stage;
  }

  /** The weapon currently being scoped, or null if unscoped. */
  getCurrentWeapon(): WeaponId | null {
    return this.currentWeapon;
  }

  /** Target FOV for the current scope state. */
  getTargetFov(): number {
    if (!this.active || !this.currentWeapon) return NORMAL_FOV;
    return computeScopeFov(this.currentWeapon, this.stage);
  }

  /**
   * Whether the full sniper overlay (circle + crosshair + mil-dots) is visible.
   * False for rifle scopes (AUG/SG553) and when unscoped.
   */
  isOverlayVisible(): boolean {
    if (!this.active || !this.currentWeapon) return false;
    return getScopeType(this.currentWeapon) === 'sniper';
  }

  /** Also a static helper so callers can check without an instance. */
  static getScopeType(weaponId: WeaponId): ScopeType {
    return getScopeType(weaponId);
  }

  /** Clean up DOM element. */
  dispose(): void {
    this.el.remove();
    this.breathHint.remove();
  }

  private updateBreathHint(): void {
    this.breathHint.style.display = this.active ? 'flex' : 'none';
  }
}
